import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { FaEdit, FaTrash, FaSave, FaTimes, FaFileAlt, FaUsers } from 'react-icons/fa';

const TeachersComponent: React.FC = () => {
    const [teachers, setTeachers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [editMode, setEditMode] = useState<number | null>(null);
    const [editedTeacherData, setEditedTeacherData] = useState<any>({});
    const [fileUpload, setFileUpload] = useState<File | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = getCookie('token');
                if (!token) {
                    console.error('No token found in cookies');
                    dispatch(setAuth(false));
                    setError('Token lama helin. Fadlan soo gal.');
                    return;
                }

                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const [userResponse, teachersResponse] = await Promise.all([
                    axios.get('/users/user'),
                    axios.get('/Branches/teachers/')
                ]);

                setUserRole(userResponse.data.user.role);
                dispatch(setAuth(true));
                setTeachers(teachersResponse.data);
            } catch (err) {
                console.error('Error fetching data:', err);
                dispatch(setAuth(false));
                if (axios.isAxiosError(err)) {
                    setError(err.response?.data?.message || err.message || 'Ku guuldareystay soo saarista xogta');
                } else {
                    setError('Khalad aan la filayn ayaa dhacay');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dispatch]);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i].trim();
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const handleEdit = (teacherId: number) => {
        setEditMode(teacherId);
        const teacherToEdit = teachers.find((teacher) => teacher.id === teacherId);
        if (teacherToEdit) {
            setEditedTeacherData({ ...teacherToEdit });
        }
    };

    const handleSave = async () => {
        if (!editMode) return;

        try {
            const token = getCookie('token');
            if (!token) throw new Error('No token found in cookies');

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            const formData = new FormData();
            if (editedTeacherData.name) formData.append('name', editedTeacherData.name);
            if (editedTeacherData.email) formData.append('email', editedTeacherData.email);
            if (editedTeacherData.phone_number) formData.append('phone_number', editedTeacherData.phone_number);
            if (editedTeacherData.salary) formData.append('salary', editedTeacherData.salary);
            if (editedTeacherData.IdNumber) formData.append('IdNumber', editedTeacherData.IdNumber);
            if (fileUpload) formData.append('file_upload', fileUpload);

            await axios.put(`/Branches/teachers/${editMode}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setTeachers(teachers.map(teacher =>
                teacher.id === editMode ? { ...teacher, ...editedTeacherData } : teacher
            ));

            setEditMode(null);
            setEditedTeacherData({});
            setFileUpload(null);
        } catch (err) {
            console.error('Error updating teacher:', err);
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.message || err.message || 'Ku guuldareystay cusbooneysiinta macalinka');
            } else {
                setError('Ku guuldareystay cusbooneysiinta macalinka');
            }
        }
    };

    const handleCancel = () => {
        setEditMode(null);
        setEditedTeacherData({});
        setFileUpload(null);
    };

    const handleDelete = async (teacherId: number) => {
        if (!window.confirm('Ma hubtaa inaad rabto inaad tirtirto macalinkan?')) return;

        try {
            const token = getCookie('token');
            if (!token) throw new Error('No token found in cookies');

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            await axios.delete(`/Branches/teachers/${teacherId}/`);
            setTeachers(teachers.filter(teacher => teacher.id !== teacherId));
        } catch (err) {
            console.error('Error deleting teacher:', err);
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.message || err.message || 'Ku guuldareystay tirtirida macalinka');
            } else {
                setError('Ku guuldareystay tirtirida macalinka');
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFileUpload(e.target.files[0]);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-content d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-content">
                <div className="alert alert-danger text-center shadow-sm border-0">{error}</div>
            </div>
        );
    }

    return (
        <div className="dashboard-content">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold text-primary mb-1">Maamulka Macalimiinta</h2>
                    <p className="text-muted mb-0">Maamul oo la soco dhamaan shaqaalaha waxbarida.</p>
                </div>
            </div>

            <div className="card-modern shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4 py-4 text-black text-uppercase small fw-bold">Magaca</th>
                                    <th className="py-4 text-black text-uppercase small fw-bold">Email-ka</th>
                                    <th className="py-4 text-black text-uppercase small fw-bold">Telefoonka</th>
                                    <th className="py-4 text-black text-uppercase small fw-bold">Mushaarka</th>
                                    <th className="py-4 text-black text-uppercase small fw-bold">Lambarka Aqoonsiga</th>
                                    <th className="py-4 text-black text-uppercase small fw-bold">Dukumentiga</th>
                                    <th className="pe-4 py-4 text-end text-black text-uppercase small fw-bold">Falalka</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teachers.length > 0 ? (
                                    teachers.map((teacher) => (
                                        <tr key={teacher.id} style={{ height: '60px' }}>
                                            <td className="ps-4 fw-medium text-dark">
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={editedTeacherData.name || ''}
                                                        onChange={(e) => setEditedTeacherData({ ...editedTeacherData, name: e.target.value })}
                                                    />
                                                ) : (
                                                    <span className="fw-bold">{teacher.name}</span>
                                                )}
                                            </td>
                                            <td>
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="email"
                                                        className="form-control form-control-sm"
                                                        value={editedTeacherData.email || ''}
                                                        onChange={(e) => setEditedTeacherData({ ...editedTeacherData, email: e.target.value })}
                                                    />
                                                ) : (
                                                    <span className="text-muted">{teacher.email}</span>
                                                )}
                                            </td>
                                            <td>
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={editedTeacherData.phone_number || ''}
                                                        onChange={(e) => setEditedTeacherData({ ...editedTeacherData, phone_number: e.target.value })}
                                                    />
                                                ) : (
                                                    <span className="text-muted">{teacher.phone_number}</span>
                                                )}
                                            </td>
                                            <td>
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={editedTeacherData.salary || ''}
                                                        onChange={(e) => setEditedTeacherData({ ...editedTeacherData, salary: e.target.value })}
                                                    />
                                                ) : (
                                                    <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3">ksh {teacher.salary}</span>
                                                )}
                                            </td>
                                            <td>
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={editedTeacherData.IdNumber || ''}
                                                        onChange={(e) => setEditedTeacherData({ ...editedTeacherData, IdNumber: e.target.value })}
                                                    />
                                                ) : (
                                                    <span className="text-muted small font-monospace">{teacher.IdNumber}</span>
                                                )}
                                            </td>
                                            <td>
                                                {editMode === teacher.id ? (
                                                    <input
                                                        type="file"
                                                        className="form-control form-control-sm"
                                                        onChange={handleFileChange}
                                                    />
                                                ) : teacher.file_upload ? (
                                                    <a
                                                        href={teacher.file_upload}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-light text-primary rounded-pill px-3"
                                                    >
                                                        <FaFileAlt className="me-1" /> Arag
                                                    </a>
                                                ) : (
                                                    <span className="text-muted small fst-italic">Eber</span>
                                                )}
                                            </td>
                                            <td className="pe-4 text-end">
                                                {editMode === teacher.id ? (
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            onClick={handleSave}
                                                            className="btn btn-sm btn-success rounded-pill px-3"
                                                        >
                                                            <FaSave className="me-1" /> Badbaadi
                                                        </button>
                                                        <button
                                                            onClick={handleCancel}
                                                            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                                        >
                                                            <FaTimes className="me-1" /> Iska Daay
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            onClick={() => handleEdit(teacher.id)}
                                                            className="btn btn-sm btn-light text-primary rounded-circle p-2 shadow-sm"
                                                            title="Edit"
                                                        >
                                                            <FaEdit size={16} />
                                                        </button>
                                                        {userRole !== 'accountant' && (
                                                            <button
                                                                onClick={() => handleDelete(teacher.id)}
                                                                className="btn btn-sm btn-light text-danger rounded-circle p-2 shadow-sm"
                                                                title="Delete"
                                                            >
                                                                <FaTrash size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="text-center py-5 text-muted">
                                            <FaUsers size={40} className="d-block mb-3 mx-auto opacity-50" />
                                            Macalimiin lama helin.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeachersComponent;